import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { DatePicker, Table, Tag, Alert, Spin, Button, Empty, Tooltip } from "antd";
import { formatPropertyName } from "../../utils/propertyDisplay";
import { useAuth } from "../../context/AuthContext";
import { CalendarOutlined, InfoCircleOutlined } from "@ant-design/icons";
import { apiFetch } from "../../services/apiClient";
import { isAdminRole } from "../../utils/roles.utils";
import dayjs from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";
import "dayjs/locale/es";
import type { ColumnsType } from "antd/es/table";
import Layout from "../../Components/Layout";
import PageHeader from "../../Components/PageHeader";
import { useHomeLogic, RentalSummary, RentalCategory } from "./useHomeLogic";
import "./home.css";

dayjs.extend(relativeTime);
dayjs.locale("es");

const { RangePicker } = DatePicker;

/**
 * Configuración visual + texto explicativo de cada estado del dashboard.
 * Las definiciones operacionales y la precedencia están en
 * `docs/modulo-home.md` §2 y son la fuente de verdad — esto solo es UI.
 *
 * Cada arriendo tiene UN estado único (mutuamente excluyente). Round
 * 2026-07-01: antes se mostraban varios tags; ahora se muestra 1 solo.
 */
const statusConfig: Record<
  RentalCategory,
  { label: string; color: string; tooltip: string }
> = {
  active: {
    label: "Activo",
    color: "#47bef5",
    tooltip:
      "En curso hoy y sin salida inminente: el huésped ya ingresó y la salida es después de mañana. No depende del rango — siempre respecto a HOY.",
  },
  upcoming: {
    label: "Por llegar",
    color: "#52c41a",
    tooltip:
      "Ingreso a partir de mañana y dentro del rango de fechas seleccionado arriba (incluye los bordes del rango).",
  },
  "ending-soon": {
    label: "Por terminar",
    color: "#ff9f60",
    tooltip:
      "La salida es HOY o MAÑANA. No depende del rango — sirve para anticipar el aseo y la preparación de la próxima reserva. Tiene prioridad sobre 'Activo' y 'Terminado'.",
  },
  completed: {
    label: "Terminado",
    color: "#9a95f8",
    tooltip:
      "La salida ya pasó (anterior a hoy) y cae dentro del rango de fechas seleccionado arriba.",
  },
};

const getColumns = (
  navigate: ReturnType<typeof useNavigate>,
): ColumnsType<RentalSummary> => [
  {
    // Sin ancho fijo + ellipsis: absorbe el espacio horizontal sobrante
    // junto con "Huésped" y trunca con tooltip los nombres largos.
    title: "Propiedad",
    dataIndex: "propertyName",
    key: "propertyName",
    ellipsis: true,
    sorter: (a, b) => a.propertyName.localeCompare(b.propertyName),
    render: (v: string) => formatPropertyName(v),
  },
  {
    title: "Huésped",
    dataIndex: "guest",
    key: "guest",
    ellipsis: true,
  },
  {
    title: "Plataforma",
    dataIndex: "platform",
    key: "platform",
    width: 110,
  },
  {
    title: "Inicio",
    dataIndex: "startDate",
    key: "startDate",
    width: 118,
    sorter: (a, b) => a.startDate.localeCompare(b.startDate),
    render: (d: string) => formatDD(d),
  },
  {
    title: "Fin",
    dataIndex: "endDate",
    key: "endDate",
    width: 118,
    sorter: (a, b) => a.endDate.localeCompare(b.endDate),
    render: (d: string) => formatDD(d),
  },
  {
    title: "Noches",
    dataIndex: "nights",
    key: "nights",
    align: "center",
    width: 90,
    sorter: (a, b) => (a.nights ?? 0) - (b.nights ?? 0),
  },
  {
    title: "Reserva total (CLP)",
    dataIndex: "price",
    key: "price",
    align: "right",
    width: 165,
    sorter: (a, b) => a.price - b.price,
    render: (v: number) => (v ? `$${Number(v).toLocaleString("es-CL")}` : "—"),
  },
  {
    title: "Estado",
    dataIndex: "category",
    key: "category",
    align: "center",
    width: 140,
    filters: [
      { text: "Activo", value: "active" },
      { text: "Por llegar", value: "upcoming" },
      { text: "Por terminar", value: "ending-soon" },
      { text: "Terminado", value: "completed" },
    ],
    // Estado único (mutuamente excluyente): filtramos por igualdad.
    onFilter: (value, record) => record.category === value,
    render: (category: RentalCategory) => {
      const cfg = statusConfig[category];
      if (!cfg) return "—";
      return (
        <Tooltip title={cfg.tooltip}>
          <Tag color={cfg.color} style={{ margin: 0 }}>
            {cfg.label}
          </Tag>
        </Tooltip>
      );
    },
  },
  {
    title: "Acciones",
    key: "acciones",
    align: "center",
    width: 150,
    render: (_: unknown, record: RentalSummary) => (
      <Button
        type="link"
        size="small"
        disabled={!record.propertyId}
        onClick={() =>
          navigate(
            `/calendario?from=${record.startDate}&to=${record.endDate}${record.propertyId ? `&propertyId=${record.propertyId}` : ""}`,
          )
        }
      >
        Ver en calendario
      </Button>
    ),
  },
];

function formatDD(d: string) {
  if (!d) return "—";
  const [y, m, day] = d.split("-");
  return `${day}-${m}-${y}`;
}

/**
 * Estado del tipo de cambio USD→CLP que el backend mantiene en cache
 * persistente. Si la API de mindicador.cl está caída, `isStale=true` y
 * mostramos un banner al admin. Ver `helpers.js` `getDolarStatus()`.
 */
type DolarStatus = {
  value: number;
  isStale: boolean;
  lastOkAt: string | null;
  lastErrorAt: string | null;
  lastErrorMessage: string | null;
};

/**
 * Hook que consulta `/api/home/dolar-status` al mount y refresca cada
 * 5 min. Solo se ejecuta para roles admin/superadmin (cliente/aseador
 * no necesitan saber del valor del dólar). Si la API admin rechaza
 * (403), `status` queda en null silenciosamente.
 */
function useDolarStatus(rol: string | null | undefined): DolarStatus | null {
  const [status, setStatus] = useState<DolarStatus | null>(null);
  useEffect(() => {
    if (!isAdminRole(rol)) return;
    let cancelled = false;
    const fetchStatus = async () => {
      const json = await apiFetch<{ data: DolarStatus }>(
        "/home/dolar-status",
      );
      if (!cancelled && json?.data) setStatus(json.data);
    };
    fetchStatus();
    const id = setInterval(fetchStatus, 5 * 60 * 1000);
    return () => {
      cancelled = true;
      clearInterval(id);
    };
  }, [rol]);
  return status;
}

const Home: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuth();

  // Aseadores no tienen dashboard en /home (ven reservas/ocupación que no les
  // aplican). Si caen acá por bookmark o redirect legacy, los mandamos a
  // /aseos donde tienen su agenda operativa.
  useEffect(() => {
    const rol = String(user?.rol ?? "").toLowerCase();
    if (rol === "aseador") navigate("/aseos", { replace: true });
  }, [user?.rol, navigate]);

  const {
    sidebarOpen,
    toggleSidebar,
    dateRange,
    handleDateRangeChange,
    filteredRentals,
    totals,
    loading,
    statusFilter,
    toggleStatusFilter,
    recentBookings,
    loadingRecent,
  } = useHomeLogic();

  const columns = getColumns(navigate);
  const dolarStatus = useDolarStatus(user?.rol);

  return (
    <Layout
      sidebarOpen={sidebarOpen}
      toggleSidebar={toggleSidebar}
      containerClass="home-container"
      contentClass="home-content"
    >
      <section className="home-dashboard">
        <PageHeader
          title="Inicio"
          subtitle="Resumen de arriendos y ocupación"
          helpText={
            "Cada arriendo tiene UN estado único, elegido por prioridad: 'Por terminar' (salida hoy o mañana) → 'Activo' (en curso, salida después de mañana) → 'Por llegar' (ingreso futuro dentro del rango) → 'Terminado' (salida pasada dentro del rango). El rango de fechas solo acota 'Por llegar' y 'Terminado'; 'Activo' y 'Por terminar' son siempre respecto a hoy. Las 4 tarjetas son excluyentes: su suma = total de filas."
          }
        />

        {/*
          Banner de estado del tipo de cambio. Solo aparece si el backend
          reporta `isStale=true` (la API de mindicador.cl está caída y se
          está usando el último valor cacheado). El cliente/aseador NO
          ve este banner — el endpoint `/api/home/dolar-status` rechaza
          con 403 a no-admin y `useDolarStatus` solo lo consume si
          `isAdminRole(rol)`.
        */}
        {dolarStatus?.isStale && (
          <Alert
            type="warning"
            showIcon
            style={{ marginBottom: 16 }}
            message="Tipo de cambio USD→CLP desactualizado"
            description={
              <div>
                <div>
                  La API de mindicador.cl no responde. Estamos usando el
                  último valor hábil cacheado:{" "}
                  <strong>${dolarStatus.value.toLocaleString("es-CL")}</strong>
                  {dolarStatus.lastOkAt && (
                    <> (capturado el {new Date(dolarStatus.lastOkAt).toLocaleString("es-CL")})</>
                  )}
                  .
                </div>
                {dolarStatus.lastErrorAt && (
                  <div style={{ fontSize: 12, opacity: 0.85, marginTop: 4 }}>
                    Último intento fallido:{" "}
                    {new Date(dolarStatus.lastErrorAt).toLocaleString("es-CL")}
                    {dolarStatus.lastErrorMessage
                      ? ` — ${dolarStatus.lastErrorMessage}`
                      : ""}
                    .
                  </div>
                )}
                <div style={{ fontSize: 12, opacity: 0.85, marginTop: 4 }}>
                  Las reservas que se crean ahora usan ese cache. Cuando la
                  API vuelva, se actualiza automáticamente.
                </div>
              </div>
            }
          />
        )}

        {/* Recently booked */}
        <div className="recent-section">
          <div className="recent-header">
            <div>
              <h3 className="recent-title">Recién agendados</h3>
              <p className="recent-subtitle">Últimas reservas creadas</p>
            </div>
          </div>
          <Spin spinning={loadingRecent}>
            {recentBookings.length === 0 && !loadingRecent ? (
              <Empty
                description="Sin reservas recientes"
                image={Empty.PRESENTED_IMAGE_SIMPLE}
              />
            ) : (
              <div className="recent-scroll">
                {recentBookings.map((b) => (
                  <div
                    className="recent-card"
                    key={b.id}
                    style={{ cursor: "pointer" }}
                    onClick={() =>
                      navigate(
                        `/calendario?from=${b.startDate}&to=${b.endDate}${b.propertyId ? `&propertyId=${b.propertyId}` : ""}`,
                      )
                    }
                  >
                    <div className="recent-card-header">
                      <span className="recent-card-property">
                        {formatPropertyName(b.propertyName)}
                      </span>
                      <Tag style={{ margin: 0, fontSize: 11 }}>
                        {b.platform}
                      </Tag>
                    </div>
                    <div className="recent-card-guest">{b.guest}</div>
                    <div className="recent-card-dates">
                      <CalendarOutlined
                        style={{ fontSize: 12, color: "#94a3b8" }}
                      />
                      <span>
                        {formatDD(b.startDate)} → {formatDD(b.endDate)}
                      </span>
                      <span className="recent-card-nights">
                        {b.nights} {b.nights === 1 ? "noche" : "noches"}
                      </span>
                    </div>
                    <div className="recent-card-footer">
                      <span className="recent-card-price">
                        {b.price
                          ? `$${Number(b.price).toLocaleString("es-CL")}`
                          : "—"}
                      </span>
                      <span className="recent-card-ago">
                        {b.createdAt && dayjs(b.createdAt).isValid()
                          ? dayjs(b.createdAt).fromNow()
                          : "—"}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </Spin>
        </div>

        {/* Date range picker */}
        <div className="home-date-bar" style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <RangePicker
            value={dateRange}
            onChange={(dates) =>
              handleDateRangeChange(dates as [any, any] | null)
            }
            format="DD-MM-YYYY"
            placeholder={["Inicio", "Fin"]}
            size="large"
            style={{ maxWidth: 380 }}
          />
          {/*
            Tooltip de ayuda al lado del RangePicker. Aclara qué
            categorías son "vivas" (relativas a hoy, ignoran el rango)
            vs cuáles sí dependen del rango. Repetimos lo que dice
            `helpText` de PageHeader pero accesible al ojo del usuario
            sin hover en el ícono del header.
          */}
          <Tooltip
            title="El rango de fechas SOLO filtra 'Por llegar' (ingresos futuros dentro del rango) y 'Terminados' (salidas pasadas dentro del rango). 'Activo' y 'Por terminar' se calculan respecto a HOY y siempre se muestran, sin importar el rango."
          >
            <InfoCircleOutlined style={{ color: "#94a3b8", fontSize: 18 }} />
          </Tooltip>
        </div>

        {!dateRange && (
          <Alert
            message="Selecciona un rango de fechas para ver los arriendos"
            type="info"
            showIcon
            style={{ marginBottom: 20, borderRadius: 10 }}
          />
        )}

        {/*
          Cards de resumen: cada una es un filtro toggleable y muestra
          el conteo de su estado. Los estados son MUTUAMENTE EXCLUYENTES
          (cada arriendo suma en 1 sola card), por eso la suma de las 4
          == total de filas de la tabla. El tooltip explica la regla
          exacta de cada estado — sincronizado con `statusConfig[*].tooltip`.
        */}
        <div className="summary-cards">
          <Tooltip title={statusConfig.active.tooltip}>
            <div
              className={`summary-card active ${statusFilter === "active" ? "is-selected" : ""}`}
              onClick={() => toggleStatusFilter("active")}
            >
              <div
                className="card-icon"
                style={{ background: "rgba(71,190,245,0.15)", color: "#47bef5" }}
              >
                🏠
              </div>
              <div className="card-data">
                <h3>{totals.activos}</h3>
                <p>Arriendos activos</p>
              </div>
            </div>
          </Tooltip>

          <Tooltip title={statusConfig.upcoming.tooltip}>
            <div
              className={`summary-card upcoming ${statusFilter === "upcoming" ? "is-selected" : ""}`}
              onClick={() => toggleStatusFilter("upcoming")}
            >
              <div
                className="card-icon"
                style={{ background: "rgba(82,196,26,0.15)", color: "#52c41a" }}
              >
                🛬
              </div>
              <div className="card-data">
                <h3>{totals.porLlegar}</h3>
                <p>Por llegar</p>
              </div>
            </div>
          </Tooltip>

          <Tooltip title={statusConfig["ending-soon"].tooltip}>
            <div
              className={`summary-card ending-soon ${statusFilter === "ending-soon" ? "is-selected" : ""}`}
              onClick={() => toggleStatusFilter("ending-soon")}
            >
              <div
                className="card-icon"
                style={{ background: "rgba(255,159,96,0.15)", color: "#ff9f60" }}
              >
                ⏱
              </div>
              <div className="card-data">
                <h3>{totals.porTerminar}</h3>
                <p>Por terminar</p>
              </div>
            </div>
          </Tooltip>

          <Tooltip title={statusConfig.completed.tooltip}>
            <div
              className={`summary-card completed ${statusFilter === "completed" ? "is-selected" : ""}`}
              onClick={() => toggleStatusFilter("completed")}
            >
              <div
                className="card-icon"
                style={{ background: "rgba(154,149,248,0.15)", color: "#9a95f8" }}
              >
                ✓
              </div>
              <div className="card-data">
                <h3>{totals.terminados}</h3>
                <p>Terminados</p>
              </div>
            </div>
          </Tooltip>
        </div>

        {/* Rentals table */}
        <div className="home-table-card">
          <Spin spinning={loading}>
            <Table<RentalSummary>
              columns={columns}
              dataSource={filteredRentals}
              rowKey="id"
              pagination={{
                defaultPageSize: 20,
                showSizeChanger: true,
                pageSizeOptions: ["10", "20", "50", "100"],
              }}
              scroll={{ x: 1000 }}
              locale={{
                emptyText: "No se encontraron arriendos en este rango",
              }}
              size="middle"
            />
          </Spin>
        </div>
      </section>
    </Layout>
  );
};

export default Home;
